import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { io } from "socket.io-client";
import notificationService from "../services/notificationService";
import roomSocketService from "../services/rooms/roomSocketService";
import communitySocketService from "../services/communitySocketService";
import { useUserContext } from "./UserContext";
import { toast } from "react-toastify";

// Collaborator notification utilities
import {
  isCollaboratorNotificationType,
  getCollaboratorNotificationMessage,
  getCollaboratorNotificationPriority,
  getCollaboratorToastTitle,
  isActionRequired as isCollaboratorActionRequired,
  getRatingStars,
  getEndorsementLevelDisplay,
} from "../components/collaborators/utils/collaboratorNotificationUtils";
import { COLLABORATOR_NOTIFICATION_TYPES } from "../constants/collaboratorNotificationConstants";

const NotificationContext = createContext();

export const useNotificationContext = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [messagingUnreadCount, setMessagingUnreadCount] = useState(0); // NEW: Messaging-specific unread
  const [socket, setSocket] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [lastNotification, setLastNotification] = useState(null);
  const roomSocketUnsubscribeRef = useRef(null);
  const communitySocketUnsubscribeRef = useRef(null);
  const { user, isAuthenticated } = useUserContext();

  // Fetch unread count on initial load and whenever user changes
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUnreadCount();
      fetchMessagingUnreadCount(); // NEW: Fetch messaging unread
    } else {
      // Reset state when user logs out
      setUnreadCount(0);
      setMessagingUnreadCount(0); // NEW: Reset messaging unread
      setNotifications([]);
      setLastNotification(null);
    }
  }, [isAuthenticated, user]);

  // Initialize socket connection for real-time notifications
  useEffect(() => {
    if (!user || (!user.token && !user.id && !user._id)) return;

    // Clean up any existing socket connection first
    if (socket) {
      socket.disconnect();
    }

    let newSocket = null;

    try {
      // Create socket connection with the correct API URL
      const socketUrl = new URL(
        process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"
      );

      // Enhanced socket configuration with better security and reconnection settings
      newSocket = io(socketUrl.origin, {
        withCredentials: true,
        path: "/socket.io/",
        auth: {
          token: user.token, // Use JWT token instead of just user ID for authentication
        },
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        autoConnect: true,
        forceNew: true,
      });

      newSocket.on("connect", () => {
        setConnectionStatus("connected");

        // Still send user ID for backward compatibility
        const userId = user.id || user._id;
        newSocket.emit("authenticate", userId);

        // Note: Don't fetch unread count here - it's already fetched on auth
        // and will be updated via socket events
      });

      newSocket.on("notification", (notification) => {
        // Sanitize notification content to prevent XSS
        const sanitizedNotification = sanitizeNotification(notification);

        // NEW: Track messaging notifications separately
        if (sanitizedNotification.type === 'direct_message') {
          setMessagingUnreadCount((prev) => prev + 1);
        }

        // Set as last notification for toast display
        setLastNotification(sanitizedNotification);

        // Show toast notification
        showNotificationToast(sanitizedNotification);

        // Add new notification to the list and update unread count atomically
        setNotifications((prev) => [sanitizedNotification, ...prev]);

        // Update unread count locally (no need to fetch from server - we track it)
        let newUnreadCount = 0;
        setUnreadCount((prev) => {
          newUnreadCount = prev + 1;
          return newUnreadCount;
        });

        // Broadcast the notification to other tabs using BroadcastChannel API
        try {
          setTimeout(() => {
            const notificationChannel = new BroadcastChannel(
              "ResearchPro_notifications"
            );
            notificationChannel.postMessage({
              type: "new_notification",
              notification: sanitizedNotification,
              unreadCount: newUnreadCount,
            });
            notificationChannel.close();
          }, 0); // Use setTimeout to ensure state has been updated
        } catch (error) {
          // BroadcastChannel not supported in this browser - handled silently
        }
      });

      // ========== WORKSHOP NOTIFICATIONS - Listen on main socket ==========
      newSocket.on("workshop_notification", (notification) => {
// Sanitize notification content
        const sanitizedNotification = sanitizeNotification(notification);
        
        // Set as last notification
        setLastNotification(sanitizedNotification);
        
        // Show toast based on priority (inline to avoid function reference issues)
        const priority = notification.priority || "normal";
        const message = notification.message || notification.title || "New workshop notification";
        
        const toastOptions = {
          position: "top-right",
          autoClose: priority === "urgent" ? 10000 : priority === "high" ? 7000 : 5000,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        };
        
        if (priority === "urgent") {
          toast.error(
            <div className="workshop-notification-toast urgent-priority">
              <strong>🚨 Workshop Alert</strong>
              <p>{message}</p>
            </div>,
            toastOptions
          );
        } else if (priority === "high") {
          toast.warning(
            <div className="workshop-notification-toast high-priority">
              <strong>🔔 Workshop</strong>
              <p>{message}</p>
            </div>,
            toastOptions
          );
        } else if (priority !== "low") {
          toast.info(
            <div className="workshop-notification-toast">
              <strong>Workshop</strong>
              <p>{message}</p>
            </div>,
            toastOptions
          );
        }
        
        // Add to notifications list
        setNotifications((prev) => [sanitizedNotification, ...prev]);
        
        // Update unread count
        let newUnreadCount = 0;
        setUnreadCount((prev) => {
          newUnreadCount = prev + 1;
          return newUnreadCount;
        });
        
        // Broadcast to other tabs
        try {
          setTimeout(() => {
            const notificationChannel = new BroadcastChannel("ResearchPro_notifications");
            notificationChannel.postMessage({
              type: "new_notification",
              notification: sanitizedNotification,
              unreadCount: newUnreadCount,
            });
            notificationChannel.close();
          }, 0);
        } catch (error) {
          // BroadcastChannel not supported
        }
      });

      // Listen for workshop notification count updates
      newSocket.on("notification_count", ({ count, source }) => {
if (typeof count === 'number') {
          setUnreadCount(count);
        }
      });

      // Handle specific notification events if needed
      newSocket.on("notification_read", (data) => {
        // Update unread count from server
        if (data.unreadCount !== undefined) {
          setUnreadCount(data.unreadCount);
        }
      });

      newSocket.on("notifications_cleared", () => {
        // Clear notifications and reset count
        setNotifications([]);
        setUnreadCount(0);
      });

      newSocket.on("disconnect", () => {
        setConnectionStatus("disconnected");
      });

      newSocket.on("error", () => {
        setConnectionStatus("error");
      });

      // Enhanced error handling
      newSocket.on("connect_error", () => {
        setConnectionStatus("error");
      });

      // Reconnection handling
      newSocket.on("reconnect", () => {
        setConnectionStatus("connected");

        // Re-authenticate and refresh data after reconnection
        newSocket.emit("authenticate", user._id);
        fetchUnreadCount();
      });

      newSocket.on("reconnect_attempt", () => {
        setConnectionStatus("connecting");
      });

      newSocket.on("reconnect_failed", () => {
        setConnectionStatus("failed");
      });

      setSocket(newSocket);

      // Set up BroadcastChannel to sync notifications across tabs
      try {
        const notificationChannel = new BroadcastChannel(
          "ResearchPro_notifications"
        );

        notificationChannel.onmessage = (event) => {
          if (event.data.type === "new_notification") {
            // Only update if this isn't the tab that originated the notification
            if (
              !lastNotification ||
              lastNotification._id !== event.data.notification._id
            ) {
              setLastNotification(event.data.notification);
              setNotifications((prev) => [event.data.notification, ...prev]);
              setUnreadCount(event.data.unreadCount);

              // Show toast in this tab too
              showNotificationToast(event.data.notification);
            }
          } else if (event.data.type === "unread_count_updated") {
            // Update unread count when it changes in another tab
            setUnreadCount(event.data.unreadCount);
          }
        };

        return () => {
          notificationChannel.close();
        };
      } catch (error) {
        // BroadcastChannel not supported - handled silently
      }
    } catch (error) {
      setConnectionStatus("error");
    }

    // Clean up on unmount
    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [user?.token]); // Only recreate socket when token changes

  // Subscribe to room socket notifications
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // Handler for room notifications from research-rooms namespace
    const handleRoomNotification = (notification) => {
      // Sanitize notification content
      const sanitizedNotification = sanitizeNotification(notification);
      
      // Set as last notification
      setLastNotification(sanitizedNotification);
      
      // Show priority-based toast
      showRoomNotificationToast(sanitizedNotification);
      
      // Add to notifications list
      setNotifications((prev) => [sanitizedNotification, ...prev]);
      
      // Update unread count
      setUnreadCount((prev) => prev + 1);
      
      // Broadcast to other tabs
      try {
        const notificationChannel = new BroadcastChannel("ResearchPro_notifications");
        notificationChannel.postMessage({
          type: "new_notification",
          notification: sanitizedNotification,
          unreadCount: unreadCount + 1,
        });
        notificationChannel.close();
      } catch (error) {
        // BroadcastChannel not supported
      }
    };

    // Subscribe to room socket notifications
    roomSocketUnsubscribeRef.current = roomSocketService.on("notification", handleRoomNotification);

    // Cleanup on unmount
    return () => {
      if (roomSocketUnsubscribeRef.current) {
        roomSocketUnsubscribeRef.current();
        roomSocketUnsubscribeRef.current = null;
      }
    };
  }, [isAuthenticated, user, unreadCount]);

  // ========== COMMUNITY SOCKET NOTIFICATIONS ==========
  // Subscribe to community socket notifications
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // Handler for community notifications from community namespace
    const handleCommunityNotification = (notification) => {
      // Sanitize notification content
      const sanitizedNotification = sanitizeNotification(notification);
      
      // Set as last notification
      setLastNotification(sanitizedNotification);
      
      // Show priority-based toast
      showCommunityNotificationToast(sanitizedNotification);
      
      // Add to notifications list
      setNotifications((prev) => [sanitizedNotification, ...prev]);
      
      // Update unread count
      setUnreadCount((prev) => prev + 1);
      
      // Broadcast to other tabs
      try {
        const notificationChannel = new BroadcastChannel("ResearchPro_notifications");
        notificationChannel.postMessage({
          type: "new_notification",
          notification: sanitizedNotification,
          unreadCount: unreadCount + 1,
        });
        notificationChannel.close();
      } catch (error) {
        // BroadcastChannel not supported
      }
    };

    // Handler for moderator-specific notifications
    const handleModeratorNotification = (notification) => {
      const sanitizedNotification = sanitizeNotification({
        ...notification,
        isModerator: true,
      });
      
      setLastNotification(sanitizedNotification);
      showCommunityNotificationToast(sanitizedNotification);
      setNotifications((prev) => [sanitizedNotification, ...prev]);
      setUnreadCount((prev) => prev + 1);
    };

    // Handler for notification count updates (badge sync)
    const handleNotificationCount = (data) => {
      const count = data?.count ?? data?.unreadCount ?? data;
      if (typeof count === 'number') {
        setUnreadCount(count);
        
        // Broadcast to other tabs
        try {
          const notificationChannel = new BroadcastChannel("ResearchPro_notifications");
          notificationChannel.postMessage({
            type: "unread_count_updated",
            unreadCount: count,
          });
          notificationChannel.close();
        } catch (error) {
          // BroadcastChannel not supported
        }
      }
    };

    // Subscribe to community socket notifications
    const unsubscribeNotification = communitySocketService.on("notification", handleCommunityNotification);
    const unsubscribeModeratorNotification = communitySocketService.on("moderator_notification", handleModeratorNotification);
    const unsubscribeNotificationCount = communitySocketService.on("notification_count", handleNotificationCount);

    // Store unsubscribe function
    communitySocketUnsubscribeRef.current = () => {
      unsubscribeNotification();
      unsubscribeModeratorNotification();
      unsubscribeNotificationCount();
    };

    // Cleanup on unmount
    return () => {
      if (communitySocketUnsubscribeRef.current) {
        communitySocketUnsubscribeRef.current();
        communitySocketUnsubscribeRef.current = null;
      }
    };
  }, [isAuthenticated, user, unreadCount]);

  // Show toast for community notifications with priority-based styling
  const showCommunityNotificationToast = (notification) => {
    if (!notification) return;

    const priority = notification.priority || "normal";
    const notificationData = notification.data || {};
    
    // Helper to format duration
    const formatDuration = (hours) => {
      if (!hours || hours <= 0) return 'permanently';
      if (hours < 24) return `for ${hours} hour${hours !== 1 ? 's' : ''}`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `for ${days} day${days !== 1 ? 's' : ''}`;
      const weeks = Math.floor(days / 7);
      return `for ${weeks} week${weeks !== 1 ? 's' : ''}`;
    };

    // Get appropriate message based on notification type
    const getMessage = () => {
      switch (notification.type) {
        // Thread & Discussion
        case "community_thread_reply":
          return `💬 ${notificationData.senderName || "Someone"} replied to your thread in ${notificationData.communityName}`;
        case "community_reply_to_reply":
          return `↩️ ${notificationData.senderName || "Someone"} replied to your comment`;
        case "community_thread_upvote":
          return `👍 Your thread received an upvote in ${notificationData.communityName}`;
        case "community_thread_pinned":
          return `📌 Your thread was pinned in ${notificationData.communityName}`;
        case "community_thread_locked":
          return `🔒 Your thread was locked in ${notificationData.communityName}`;
        case "community_reaction_received":
          return `${notificationData.reactionType || "🏆"} Your thread received an award!`;
        case "community_thread_update":
          return `🔔 New activity in a thread you follow`;
        case "community_mention":
          return `@${notificationData.senderName || "Someone"} mentioned you in ${notificationData.communityName}`;
        case "community_chat_mention":
          return `💬@ ${notificationData.senderName || "Someone"} mentioned you in chat`;
        
        // Membership
        case "community_join_request":
          return `👋 ${notificationData.requesterName || "Someone"} wants to join ${notificationData.communityName}`;
        case "community_join_accepted":
          return `✅ Welcome! Your request to join ${notificationData.communityName} was approved`;
        case "community_join_rejected":
          return `❌ Your request to join ${notificationData.communityName} was not approved`;
        case "community_member_promoted":
          return `⬆️ You are now a moderator of ${notificationData.communityName}! 🎉`;
        case "community_member_demoted":
          return `⬇️ You are no longer a moderator of ${notificationData.communityName}`;
        case "community_member_removed":
          return `🚫 You have been removed from ${notificationData.communityName}${notificationData.reason ? `: ${notificationData.reason}` : ''}`;
        
        // Moderation
        case "community_moderation_warn":
          return `⚠️ You received a warning in ${notificationData.communityName}${notificationData.reason ? `: ${notificationData.reason}` : ''}`;
        case "community_moderation_mute":
          return `🔇 You have been muted ${formatDuration(notificationData.duration)} in ${notificationData.communityName}${notificationData.reason ? `: ${notificationData.reason}` : ''}`;
        case "community_moderation_ban":
          return `🚫 You have been banned ${formatDuration(notificationData.duration)} from ${notificationData.communityName}${notificationData.reason ? `: ${notificationData.reason}` : ''}`;
        case "community_content_removed":
          return `🗑️ Your content was removed from ${notificationData.communityName}${notificationData.reason ? `: ${notificationData.reason}` : ''}`;
        case "community_new_report":
          return `🚨 New report submitted in ${notificationData.communityName}`;
        
        // Community-wide
        case "community_announcement":
          return `📢 Announcement in ${notificationData.communityName}: ${notificationData.title || 'New announcement'}`;
        case "community_settings_updated":
          return `⚙️ Settings updated in ${notificationData.communityName}`;
        case "community_new_room":
          return `🏠 New room "${notificationData.roomName}" created in ${notificationData.communityName}`;
        
        default:
          return notification.message || "New community notification";
      }
    };

    const message = getMessage();
    
    // Priority-based toast options
    const toastOptions = {
      position: "top-right",
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    };

    if (priority === "high") {
      // High priority - longer duration, more prominent
      toast.warning(
        <div className="community-notification-toast high-priority">
          <strong>🏘️ Community Alert</strong>
          <p>{message}</p>
        </div>,
        { ...toastOptions, autoClose: 10000 }
      );
    } else if (priority === "low") {
      // Low priority - shorter duration, subtle
      toast.info(message, { ...toastOptions, autoClose: 3000 });
    } else {
      // Normal priority
      toast.info(
        <div className="community-notification-toast">
          <strong>Community</strong>
          <p>{message}</p>
        </div>,
        { ...toastOptions, autoClose: 5000 }
      );
    }
  };

  // Show toast for room notifications with priority-based styling
  const showRoomNotificationToast = (notification) => {
    if (!notification) return;

    const priority = notification.priority || "normal";
    const notificationData = notification.data || {};
    
    // Get appropriate message based on notification type
    const getMessage = () => {
      switch (notification.type) {
        case "join_request_received":
          return `${notificationData.requesterName} wants to join ${notificationData.roomName}`;
        case "join_request_accepted":
          return `🎉 You've been accepted to ${notificationData.roomName}!`;
        case "join_request_rejected":
          return `Your request to join ${notificationData.roomName} was declined`;
        case "member_removed":
          return `You've been removed from ${notificationData.roomName}`;
        case "member_left":
          return `${notificationData.memberName} left ${notificationData.roomName}`;
        case "member_joined":
          return `${notificationData.memberName} joined ${notificationData.roomName} 🎉`;
        case "room_deleted":
          return `${notificationData.roomName} has been deleted`;
        case "room_updated":
          return `${notificationData.roomName} settings updated`;
        case "event_created":
          return `📅 New event: ${notificationData.eventTitle}`;
        case "event_updated":
          return `📝 Event updated: ${notificationData.eventTitle}`;
        case "event_deleted":
          return `❌ Event cancelled: ${notificationData.eventTitle}`;
        case "event_starting_soon":
          return `🔔 ${notificationData.eventTitle} starting in ${notificationData.minutesBefore} minutes!`;
        default:
          return notification.message || "New room notification";
      }
    };

    const message = getMessage();
    
    // Priority-based toast options
    const toastOptions = {
      position: "top-right",
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    };

    if (priority === "high") {
      // High priority - longer duration, more prominent
      toast.warning(
        <div className="room-notification-toast high-priority">
          <strong>{notification.type === "event_starting_soon" ? "⏰ Meeting Reminder" : "🔔 Room Alert"}</strong>
          <p>{message}</p>
        </div>,
        { ...toastOptions, autoClose: 10000 }
      );
      
      // Play sound for meeting reminders
      if (notification.type === "event_starting_soon") {
        try {
          new Audio("/sounds/meeting-reminder.mp3").play().catch(() => {});
        } catch (e) {
          // Audio not available
        }
      }
    } else if (priority === "low") {
      // Low priority - shorter duration, subtle
      toast.info(message, { ...toastOptions, autoClose: 3000 });
    } else {
      // Normal priority
      toast.info(
        <div className="room-notification-toast">
          <strong>Research Room</strong>
          <p>{message}</p>
        </div>,
        { ...toastOptions, autoClose: 5000 }
      );
    }
  };

  // ========== COLLABORATOR NOTIFICATION TOAST ==========
  // Show toast for collaborator notifications (follow, collab requests, ratings, endorsements)
  const showCollaboratorNotificationToast = (notification) => {
    if (!notification) return;

    const { type, data = {} } = notification;
    const priority = getCollaboratorNotificationPriority(notification);
    const title = getCollaboratorToastTitle(type);
    const message = getCollaboratorNotificationMessage(notification);
    const actionRequired = isCollaboratorActionRequired(type);

    // Priority-based toast options
    const toastOptions = {
      position: "top-right",
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      autoClose: priority === "high" ? 7000 : priority === "low" ? 3000 : 5000,
    };

    // Build toast content based on notification type
    const renderToastContent = () => {
      switch (type) {
        case COLLABORATOR_NOTIFICATION_TYPES.RATING_RECEIVED:
          return (
            <div className="collaborator-notification-toast rating-toast">
              <strong>{title}</strong>
              <p>{message}</p>
              {data.overallRating && (
                <span className="rating-stars">{getRatingStars(data.overallRating)}</span>
              )}
              {data.feedbackPreview && (
                <p className="feedback-preview">"{data.feedbackPreview}..."</p>
              )}
            </div>
          );

        case COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_RECEIVED:
          return (
            <div className="collaborator-notification-toast endorsement-toast">
              <strong>{title}</strong>
              <p>{message}</p>
              {data.endorsementLevel && (
                <span className="endorsement-level">
                  {getEndorsementLevelDisplay(data.endorsementLevel)}
                </span>
              )}
              {actionRequired && (
                <span className="action-hint">Tap to respond</span>
              )}
            </div>
          );

        case COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED:
          return (
            <div className="collaborator-notification-toast collab-request-toast high-priority">
              <strong>{title}</strong>
              <p>{message}</p>
              {data.messagePreview && (
                <p className="message-preview">"{data.messagePreview}"</p>
              )}
              <span className="action-hint">Tap to respond</span>
            </div>
          );

        case COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_ACCEPTED:
          return (
            <div className="collaborator-notification-toast collab-accepted-toast high-priority">
              <strong>{title}</strong>
              <p>{message}</p>
              <span className="action-hint">Tap to go to room</span>
            </div>
          );

        default:
          return (
            <div className="collaborator-notification-toast">
              <strong>{title}</strong>
              <p>{message}</p>
            </div>
          );
      }
    };

    // Show toast based on priority
    if (priority === "high") {
      toast.warning(renderToastContent(), toastOptions);
    } else if (priority === "low") {
      // Low priority - only show if not dismissible types
      if (type !== COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_DECLINED) {
        toast.info(renderToastContent(), { ...toastOptions, autoClose: 3000 });
      }
    } else {
      toast.info(renderToastContent(), toastOptions);
    }
  };

  // Show toast notification
  const showNotificationToast = (notification) => {
    if (!notification) return;

    // Check if it's a collaborator notification - use specialized toast
    if (isCollaboratorNotificationType(notification.type)) {
      showCollaboratorNotificationToast(notification);
      return;
    }

    // Create a simple toast notification for other types
    toast.info(
      <div className="notification-toast">
        <div className="notification-toast-header">
          <strong>{notification.sender?.name || "New notification"}</strong>
        </div>
        <div className="notification-toast-body">{notification.message}</div>
      </div>,
      {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      }
    );
  };

  // Simple XSS prevention for notification content
  const sanitizeNotification = (notification) => {
    if (!notification) return notification;

    // Create a new object to avoid mutating the original
    const sanitized = { ...notification };

    // Sanitize text fields
    if (sanitized.message) {
      sanitized.message = sanitized.message
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    }

    // Sanitize post content if exists
    if (sanitized.post && sanitized.post.content) {
      sanitized.post.content = sanitized.post.content
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    }

    return sanitized;
  };

  // Function to fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated || !user) return null;

    try {
      const response = await notificationService.getUnreadCount();

      if (response && response.unreadCount !== undefined) {
        setUnreadCount(response.unreadCount);
      }

      return response;
    } catch (error) {
      throw error;
    }
  }, [isAuthenticated, user]);

  // NEW: Function to fetch messaging unread count
  const fetchMessagingUnreadCount = useCallback(async () => {
    if (!isAuthenticated || !user) return null;

    try {
      const response = await notificationService.getMessagingUnreadCount();

      if (response && response.success && response.data) {
        setMessagingUnreadCount(response.data.totalUnread || 0);
      }

      return response;
    } catch (error) {
// Silently fail - don't disrupt user experience
      return null;
    }
  }, [isAuthenticated, user]);

  // Function to refresh unread count - used by components
  const refreshUnreadCount = useCallback(() => {
    if (!isAuthenticated || !user) return;

    // First try to get from API
    fetchUnreadCount()
      .then((response) => {
        // If we got a response with unreadCount, broadcast to other tabs
        if (response && response.unreadCount !== undefined) {
          try {
            const notificationChannel = new BroadcastChannel(
              "ResearchPro_notifications"
            );
            notificationChannel.postMessage({
              type: "unread_count_updated",
              unreadCount: response.unreadCount,
            });
            notificationChannel.close();
          } catch (error) {
            // BroadcastChannel not supported - handled silently
          }
        }
      })
      .catch(() => {
        // If API fails, try to get from socket
        if (socket && socket.connected) {
          socket.emit("get_unread_count");
        }
      });
  }, [isAuthenticated, user, fetchUnreadCount, socket]);

  // Function to update unread count - used by components
  const updateUnreadCount = useCallback((count) => {
    setUnreadCount(count);

    // Broadcast the updated count to other tabs
    try {
      const notificationChannel = new BroadcastChannel("ResearchPro_notifications");
      notificationChannel.postMessage({
        type: "unread_count_updated",
        unreadCount: count,
      });
      notificationChannel.close();
    } catch (error) {
      // BroadcastChannel not supported - handled silently
    }
  }, []);

  // Reset unread count when visiting notifications page
  const resetUnreadCount = useCallback(() => {
    setUnreadCount(0);
    return notificationService.markAllAsRead();
  }, []);

  // NEW: Clear messaging unread count (decrement by count)
  const clearMessagingUnread = useCallback((count = 1) => {
    setMessagingUnreadCount((prev) => Math.max(0, prev - count));
  }, []);

  // NEW: Reset messaging unread count completely
  const resetMessagingUnread = useCallback(() => {
    setMessagingUnreadCount(0);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        unreadCount,
        messagingUnreadCount, // NEW: Messaging-specific unread count
        updateUnreadCount,
        fetchUnreadCount,
        fetchMessagingUnreadCount, // NEW: Fetch messaging unread
        refreshUnreadCount,
        resetUnreadCount,
        clearMessagingUnread, // NEW: Clear messaging unread
        resetMessagingUnread, // NEW: Reset messaging unread completely
        socket,
        notifications,
        setNotifications,
        connectionStatus,
        lastNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
